import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import "./App.css";
import "swiper/css";
import "swiper/css/effect-fade";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { SiteLayout } from "./components/SiteLayout";
import { detectLocale, getLocaleUi, getLocalizedSiteBundle, LOCALE_OPTIONS } from "./content/locales";
import { getBackendBaseCandidates, getBackendBaseUrl, persistBackendBaseUrl } from "./lib/utils";

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAuthCallback = lazy(() => import("./pages/AdminDashboard").then((module) => ({ default: module.AdminAuthCallback })));
const AdminCelosiasPage = lazy(() => import("./pages/AdminCelosiasPage"));
const SalesDashboardPage = lazy(() => import("./pages/SalesDashboardPage"));
const SalesAuthCallback = lazy(() => import("./pages/SalesDashboardPage").then((module) => ({ default: module.SalesAuthCallback })));
const SalesQuoteComposerPage = lazy(() => import("./pages/SalesQuoteComposerPage"));
const SalesQuoteDetailPage = lazy(() => import("./pages/SalesQuoteDetailPage"));
const ClientPortalLoginPage = lazy(() => import("./pages/ClientPortalLoginPage"));
const ClientPortalDashboardPage = lazy(() => import("./pages/ClientPortalDashboardPage"));
const ClientPortalQuoteDetailPage = lazy(() => import("./pages/ClientPortalQuoteDetailPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const CollectionsPage = lazy(() => import("./pages/CollectionsPage"));
const CollectionDetailPage = lazy(() => import("./pages/CollectionDetailPage"));
const DesignDetailPage = lazy(() => import("./pages/DesignDetailPage"));
const DetailPage = lazy(() => import("./pages/DetailPage"));
const ResourceCenterPage = lazy(() => import("./pages/ResourceCenterPage"));
const ColorsPage = lazy(() => import("./pages/ColorsPage"));
const FinishesPage = lazy(() => import("./pages/FinishesPage"));
const ConfiguratorPage = lazy(() => import("./pages/ConfiguratorPage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const CmsPageRoutePage = lazy(() => import("./pages/CmsPageRoutePage"));
const CmsStructuredBridgePage = lazy(() => import("./pages/CmsStructuredBridgePage"));
const CmsPreviewPage = lazy(() => import("./pages/CmsPreviewPage"));

const withRouteBoundary = (element) => <RouteErrorBoundary>{element}</RouteErrorBoundary>;

const SITE_CACHE_KEY = "arista-site-content-cache-v2";
const SITE_CACHE_MAX_AGE_MS = 90 * 1000;

const readCachedSiteEntry = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(SITE_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.data || typeof parsed.data !== "object") {
      return null;
    }

    return {
      data: parsed.data,
      savedAt: Number(parsed.savedAt) || 0,
      etag: typeof parsed.etag === "string" ? parsed.etag : "",
    };
  } catch {
    return null;
  }
};

const isFreshSiteCacheEntry = (entry) => {
  if (!entry?.data) {
    return false;
  }

  return Date.now() - (entry.savedAt || 0) <= SITE_CACHE_MAX_AGE_MS;
};

const persistCachedSiteData = ({ data, etag = "" }) => {
  if (typeof window === "undefined" || !data) {
    return;
  }

  try {
    window.localStorage.setItem(SITE_CACHE_KEY, JSON.stringify({ data, etag, savedAt: Date.now() }));
  } catch {
    // noop
  }
};

const getInitialCachedSiteData = () => {
  const entry = readCachedSiteEntry();
  return isFreshSiteCacheEntry(entry) ? entry.data : null;
};

function LoadingShell({ title }) {
  return (
    <div className="loading-shell" data-testid="loading-shell">
      <div className="loading-panel">
        <span className="loading-kicker" data-testid="loading-kicker">
          Arista + Vértice
        </span>
        <h1 className="loading-title" data-testid="loading-title">
          {title}
        </h1>
      </div>
    </div>
  );
}

function AppRouter({ localizedData, ui, locale, setLocale, publicApiUrl, internalApiUrl }) {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const adminApiUrl = "/api";

  if (["/admin", "/admin-celosias"].includes(normalizedPath) && location.hash?.includes("session_id=")) {
    return <AdminAuthCallback apiUrl={adminApiUrl} />;
  }

  if ((normalizedPath === "/ventas" || normalizedPath.startsWith("/admin/sales")) && location.hash?.includes("session_id=")) {
    return withRouteBoundary(<SalesAuthCallback apiUrl={internalApiUrl} basePath={normalizedPath.startsWith("/admin/sales") ? "/admin/sales" : "/ventas"} />);
  }

  return (
    <Suspense fallback={<LoadingShell title={ui.app.loadingTitle} />}>
      <Routes>
        <Route path="/admin/*" element={<AdminDashboard apiUrl={adminApiUrl} />} />
        <Route path="/admin-celosias" element={<AdminCelosiasPage apiUrl={adminApiUrl} />} />
        <Route path="/ventas" element={withRouteBoundary(<SalesDashboardPage apiUrl={internalApiUrl} defaultView="dashboard" />)} />
        <Route path="/admin/sales" element={withRouteBoundary(<SalesDashboardPage apiUrl={internalApiUrl} defaultView="dashboard" />)} />
        <Route path="/admin/sales/quotes" element={withRouteBoundary(<SalesDashboardPage apiUrl={internalApiUrl} defaultView="quotes" />)} />
        <Route path="/admin/sales/quotes/new" element={withRouteBoundary(<SalesQuoteComposerPage apiUrl={internalApiUrl} />)} />
        <Route path="/admin/sales/quotes/:quoteId" element={withRouteBoundary(<SalesQuoteDetailPage apiUrl={internalApiUrl} />)} />
        <Route path="/client-portal/login" element={withRouteBoundary(<ClientPortalLoginPage apiUrl={internalApiUrl} />)} />
        <Route path="/client-portal" element={withRouteBoundary(<ClientPortalDashboardPage apiUrl={internalApiUrl} />)} />
        <Route path="/client-portal/quotes/:quoteId" element={withRouteBoundary(<ClientPortalQuoteDetailPage apiUrl={internalApiUrl} />)} />
        <Route path="/preview/page/:pageId" element={<CmsPreviewPage apiUrl={publicApiUrl} locale={locale} />} />
        <Route path="/" element={<SiteLayout data={localizedData} ui={ui} locale={locale} setLocale={setLocale} localeOptions={LOCALE_OPTIONS} />}>
          <Route index element={<HomePage data={localizedData} ui={ui} locale={locale} />} />
          <Route path="buscar" element={<Navigate to="/" replace />} />
          <Route path="colecciones" element={<CollectionsPage data={localizedData} ui={ui} />} />
          <Route path="colecciones/:collectionId" element={<CollectionDetailPage data={localizedData} ui={ui} locale={locale} apiUrl={publicApiUrl} />} />
          <Route path="muro/:designId" element={<DesignDetailPage data={localizedData} locale={locale} collectionId="muro" apiUrl={publicApiUrl} />} />
          <Route path="celosias/:designId" element={<DesignDetailPage data={localizedData} locale={locale} collectionId="celosias" apiUrl={publicApiUrl} />} />
          <Route path="paneles/:designId" element={<DesignDetailPage data={localizedData} locale={locale} collectionId="paneles" apiUrl={publicApiUrl} />} />
          <Route path="pisos/:designId" element={<DesignDetailPage data={localizedData} locale={locale} collectionId="pisos" apiUrl={publicApiUrl} />} />
          <Route
            path="catalogos"
            element={<ResourceCenterPage data={localizedData} pageKey="catalogs" apiUrl={publicApiUrl} ui={ui} />}
          />
          <Route
            path="texturas"
            element={<CmsStructuredBridgePage apiUrl={publicApiUrl} locale={locale} data={localizedData} ui={ui} fallback={<DetailPage data={localizedData} pageKey="textures" apiUrl={publicApiUrl} ui={ui} locale={locale} />} />}
          />
          <Route
            path="colores"
            element={<ColorsPage data={localizedData} ui={ui} />}
          />
          <Route
            path="manuales"
            element={<ResourceCenterPage data={localizedData} pageKey="manuals" apiUrl={publicApiUrl} ui={ui} />}
          />
          <Route path="acabados" element={<FinishesPage ui={ui} />} />
          <Route path="configurador" element={<ConfiguratorPage data={localizedData} ui={ui} locale={locale} />} />
          <Route path="losetas-de-concreto" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="losetas-de-concreto" />} />
          <Route path="celosias-de-concreto" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="celosias-de-concreto" />} />
          <Route path="celosias" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="celosias-de-concreto" />} />
          <Route path="paneles-de-concreto" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="paneles-de-concreto" />} />
          <Route path="revestimientos-para-muros" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="revestimientos-para-muros" />} />
          <Route path="acabados-arquitectonicos" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="acabados-arquitectonicos" />} />
          <Route path="wall-tiles" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="wall-tiles" />} />
          <Route path="concrete-wall-tiles" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="wall-tiles" />} />
          <Route path="concrete-screens" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="concrete-screens" />} />
          <Route path="decorative-concrete-panels" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="decorative-concrete-panels" />} />
          <Route path="architectural-concrete-finishes" element={<SeoLandingPage data={localizedData} ui={ui} locale={locale} slug="architectural-concrete-finishes" />} />
          <Route
            path="aliados"
            element={<CmsStructuredBridgePage apiUrl={publicApiUrl} locale={locale} data={localizedData} ui={ui} fallback={<DetailPage data={localizedData} pageKey="allies" apiUrl={publicApiUrl} ui={ui} />} />}
          />
          <Route
            path="contacto"
            element={<CmsStructuredBridgePage apiUrl={publicApiUrl} locale={locale} data={localizedData} ui={ui} fallback={<DetailPage data={localizedData} pageKey="contact" apiUrl={publicApiUrl} ui={ui} locale={locale} />} />}
          />
          <Route path="*" element={<CmsPageRoutePage apiUrl={publicApiUrl} locale={locale} />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  const [siteData, setSiteData] = useState(() => getInitialCachedSiteData());
  const [loading, setLoading] = useState(() => !getInitialCachedSiteData());
  const [error, setError] = useState(false);
  const [publicApiUrl, setPublicApiUrl] = useState(() => `${getBackendBaseUrl()}/api`);
  const [locale, setLocale] = useState(() => {
    if (typeof window === "undefined") {
      return "es-MX";
    }

    return window.localStorage.getItem("arista-locale") || detectLocale();
  });

  const localizedBundle = useMemo(() => (siteData ? getLocalizedSiteBundle(siteData, locale) : null), [siteData, locale]);
  const ui = localizedBundle?.ui || getLocaleUi(locale);
  const localizedData = localizedBundle?.data || null;

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const usesCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
    const isCompactViewport = window.innerWidth < 1024;

    if (prefersReducedMotion || usesCoarsePointer || isCompactViewport) {
      return undefined;
    }

    const lenis = new Lenis({
      autoRaf: false,
      duration: 0.82,
      smoothWheel: true,
    });

    let animationFrameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      animationFrameId = requestAnimationFrame(raf);
    };

    animationFrameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(animationFrameId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const loadSite = async () => {
      const cachedEntry = readCachedSiteEntry();
      const fallbackData = cachedEntry?.data || null;
      const canHydrateFromCache = isFreshSiteCacheEntry(cachedEntry);
      const retryDelays = [0, 450, 1200];
      const backendCandidates = getBackendBaseCandidates();

      if (canHydrateFromCache && fallbackData) {
        setSiteData(fallbackData);
        setLoading(false);
      }

      for (let index = 0; index < retryDelays.length; index += 1) {
        if (retryDelays[index] > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, retryDelays[index]));
        }

        for (const backendBaseUrl of backendCandidates) {
          try {
            const response = await axios.get(`${backendBaseUrl}/api/site-content`, {
              timeout: 8000,
              headers: {
                Accept: "application/json",
                ...(cachedEntry?.etag ? { "If-None-Match": cachedEntry.etag } : {}),
              },
              validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
            });

            if (response.status === 304 && fallbackData) {
              persistBackendBaseUrl(backendBaseUrl);
              setPublicApiUrl(`${backendBaseUrl}/api`);
              setSiteData(fallbackData);
              setError(false);
              setLoading(false);
              return;
            }

            persistBackendBaseUrl(backendBaseUrl);
            setPublicApiUrl(`${backendBaseUrl}/api`);
            setSiteData(response.data);
            persistCachedSiteData({ data: response.data, etag: response.headers?.etag || "" });
            setError(false);
            setLoading(false);
            return;
          } catch (loadError) {
            console.error(loadError);
          }
        }

        if (index === retryDelays.length - 1) {
          if (fallbackData) {
            setSiteData(fallbackData);
            setError(false);
            setLoading(false);
            return;
          }
          setError(true);
          setLoading(false);
        }
      }
    };

    void loadSite();
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("arista-locale", locale);
    }
  }, [locale]);

  const internalApiUrl = `${getBackendBaseUrl()}/api`;

  if (loading) {
    return <LoadingShell title={ui.app.loadingTitle} />;
  }

  if (error || !siteData) {
    return (
      <div className="loading-shell" data-testid="site-error-shell">
        <div className="loading-panel">
          <span className="loading-kicker" data-testid="site-error-kicker">
            Arista + Vértice
          </span>
          <h1 className="loading-title" data-testid="site-error-title">
            {ui.app.errorTitle}
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="site-shell">
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <AppRouter localizedData={localizedData} ui={ui} locale={locale} setLocale={setLocale} publicApiUrl={publicApiUrl} internalApiUrl={internalApiUrl} />
      </BrowserRouter>
    </div>
  );
}

export default App;
